import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ScoreRing } from "../../../components/ui";
import { getInterview } from "../../../api/interview";

const recommendationTone = (r) => {
  const v = String(r || "").toLowerCase();
  if (v.includes("strong no hire") || v === "no hire" || v.includes("no hire"))
    return { bg: "bg-red-100", fg: "text-red-700", border: "border-red-200" };
  if (v.includes("strong hire") || v === "hire")
    return { bg: "bg-emerald-100", fg: "text-emerald-700", border: "border-emerald-200" };
  if (v.includes("lean"))
    return { bg: "bg-amber-100", fg: "text-amber-700", border: "border-amber-200" };
  return { bg: "bg-slate-100", fg: "text-slate-600", border: "border-slate-200" };
};

const fmtMmSs = (s) => {
  const n = Number(s);
  if (!isFinite(n) || n < 0) return "";
  const m = Math.floor(n / 60);
  const sec = Math.floor(n % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const fmtDuration = (s) => {
  const n = Number(s);
  if (!isFinite(n) || n <= 0) return "—";
  const m = Math.floor(n / 60);
  const sec = Math.floor(n % 60);
  return `${m}m ${sec}s`;
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return "—";
  const days = Math.floor(ms / 86_400_000);
  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const mins = Math.floor(ms / 60_000);
  if (mins > 0) return `${mins} min${mins > 1 ? "s" : ""} ago`;
  return "just now";
};

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const speakerLabel = (role) => {
  const r = String(role || "").toLowerCase();
  if (r === "assistant" || r === "interviewer") return "Interviewer";
  if (r === "user" || r === "candidate") return "Candidate";
  return role || "Speaker";
};

const SparkleIcon = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
    <path d="M19 14l.8 2L22 16.8l-2 .8L19 20l-.8-2L16 16.8l2-.8L19 14z" />
  </svg>
);

const DocIcon = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8M8 17h6" />
  </svg>
);

const ExternalLinkIcon = (props) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const WarningIcon = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ChecklistIcon = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 11l2 2 4-4" />
    <path d="M9 17l2 2 4-4" />
    <line x1="3" y1="6" x2="5" y2="6" />
    <line x1="3" y1="12" x2="5" y2="12" />
    <line x1="3" y1="18" x2="5" y2="18" />
  </svg>
);

const CheckIcon = (props) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Avatar = ({ kind }) => {
  const isInt = kind === "Interviewer";
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
        isInt
          ? "bg-violet-100 text-violet-700"
          : "bg-emerald-100 text-emerald-700"
      }`}
    >
      {isInt ? "I" : "C"}
    </div>
  );
};

const SectionCard = ({ children, className = "" }) => (
  <section
    className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`}
  >
    {children}
  </section>
);

const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex flex-col">
    <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {label}
    </dt>
    <dd
      className={`mt-0.5 break-words text-sm text-slate-900 ${
        mono ? "font-mono text-[12px]" : ""
      }`}
    >
      {value || "—"}
    </dd>
  </div>
);

// ---------------------------------------------------------------------------
// Integrity (proctoring)
// ---------------------------------------------------------------------------

// How each signal reads to a human reviewer. `severity` drives colour only — it
// is deliberately never summed into a score. Browser-side signals aren't strong
// enough to carry a rejection on their own; they exist to flag an interview for
// a person to look at, and a number would invite treating them as a verdict.
const PROCTOR_EVENT_META = {
  away: { label: "Left the interview tab", severity: "medium" },
  fullscreen_exit: { label: "Exited fullscreen", severity: "medium" },
  monitor_attached: { label: "Second display attached", severity: "high" },
  monitor_detached: { label: "Second display removed", severity: "low" },
  screenshare_stopped: { label: "Screen share stopped", severity: "high" },
  screenshare_restored: { label: "Screen share restored", severity: "low" },
  virtual_device: { label: "Virtual camera / microphone", severity: "high" },
  media_disabled: { label: "Camera or mic turned off", severity: "medium" },
  paste: { label: "Pasted text", severity: "medium" },
  copy: { label: "Copied text", severity: "medium" },
  resize: { label: "Window resized", severity: "low" },
  session_start: { label: "Session started", severity: "info" },
  heartbeat: { label: "Heartbeat", severity: "info" },
};

const SEVERITY_DOT = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-slate-300",
  info: "bg-slate-300",
};

// Routine bookkeeping — meaningful in aggregate (it's what proves the reporter
// was alive) but noise in a timeline a human reads.
const TIMELINE_HIDDEN = new Set(["heartbeat", "session_start"]);

const fmtMs = (ms) => {
  const n = Number(ms);
  if (!isFinite(n) || n <= 0) return null;
  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 60_000) return `${(n / 1000).toFixed(1)}s`;
  const m = Math.floor(n / 60_000);
  const s = Math.round((n % 60_000) / 1000);
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

const clockTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// One short clause describing what actually happened, from the event's detail
// blob. Returns null when there is nothing worth adding beyond the label.
const proctorDetailText = (e) => {
  const d = e.detail || {};
  const parts = [];
  const dur = fmtMs(e.duration_ms);
  if (dur) parts.push(`for ${dur}`);
  if (e.type === "away" && d.since_last_ms) {
    parts.push(`${fmtMs(d.since_last_ms)} after the previous one`);
  }
  if ((e.type === "paste" || e.type === "copy") && typeof d.length === "number") {
    parts.push(`${d.length} characters${d.target ? ` in ${d.target}` : ""}`);
  }
  if (e.type === "virtual_device") {
    const names = [...(d.labels || []), ...(d.video || []), ...(d.audio || [])];
    if (names.length) parts.push(names.join(", "));
    if (d.mid_call) parts.push("switched mid-interview");
  }
  if (e.type === "screenshare_stopped" && d.escalated) {
    parts.push("not restored — interview ended");
  }
  if (e.type === "resize" && d.from && d.to) {
    parts.push(`${d.from} → ${d.to}${d.shrunk ? " (smaller)" : ""}`);
  }
  if (e.type === "media_disabled") {
    const off = [];
    if (d.camera === false) off.push("camera");
    if (d.microphone === false) off.push("microphone");
    if (off.length) parts.push(off.join(" and "));
  }
  return parts.length ? parts.join(" · ") : null;
};

/**
 * Integrity panel.
 *
 * Three states, and the difference between them matters:
 *   - the call failed        → we don't know; say so
 *   - no telemetry reported  → NOT a pass. Either the interview predates
 *                              proctoring, or the reporter was blocked. Both
 *                              mean unobserved, and showing "clean" would be a
 *                              lie a reviewer would act on.
 *   - events present         → summary + timeline
 */
const IntegritySection = ({ data, error, loading }) => {
  const summary = data?.summary;
  const events = Array.isArray(data?.events) ? data.events : [];
  const timeline = events.filter((e) => !TIMELINE_HIDDEN.has(e.type));

  let body;
  if (error) {
    body = (
      <p className="text-sm text-slate-500">
        Integrity data could not be loaded, so this interview is unverified.
      </p>
    );
  } else if (loading && !data) {
    body = <p className="text-sm text-slate-400">Loading…</p>;
  } else if (!summary || !summary.reporting_healthy) {
    body = (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
        <p className="text-sm font-semibold text-amber-900">No monitoring data reported</p>
        <p className="mt-1 text-xs leading-relaxed text-amber-800">
          This interview sent no proctoring heartbeats. It either ran before monitoring was
          enabled, or the candidate&apos;s browser did not report. Treat it as unobserved
          rather than clean.
        </p>
      </div>
    );
  } else {
    const counts = summary.counts || {};
    const awayCount = counts.away || 0;
    body = (
      <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              summary.flagged_events > 0
                ? "bg-amber-100 text-amber-800"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {summary.flagged_events > 0
              ? `${summary.flagged_events} flagged event${summary.flagged_events > 1 ? "s" : ""}`
              : "No flagged events"}
          </span>
          {awayCount > 0 && (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              {fmtMs(summary.total_away_ms)} away over {awayCount} time
              {awayCount > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {timeline.length === 0 ? (
          <p className="text-sm text-slate-500">
            Monitoring reported normally and recorded nothing of note.
          </p>
        ) : (
          <ol className="space-y-2.5">
            {timeline.map((e, i) => {
              const meta = PROCTOR_EVENT_META[e.type] || {
                label: titleCase(e.type),
                severity: "low",
              };
              const detail = proctorDetailText(e);
              return (
                <li key={`${e.seq ?? i}-${e.type}`} className="flex gap-2.5">
                  <span
                    className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                      SEVERITY_DOT[meta.severity] || SEVERITY_DOT.low
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm leading-snug text-slate-900">{meta.label}</p>
                    {detail && (
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{detail}</p>
                    )}
                  </div>
                  <span className="ml-auto shrink-0 font-mono text-[11px] text-slate-400">
                    {clockTime(e.received_at)}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </>
    );
  }

  return (
    <SectionCard>
      <header className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <WarningIcon />
        Integrity
      </header>
      {body}
      <p className="mt-3 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
        Browser-side signals only. They flag an interview for review — they are not proof of
        cheating and cannot see a second device.
      </p>
    </SectionCard>
  );
};

export default function InterviewReport() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const id = params.get("id");

  const [details, setDetails] = useState(null);
  const [report, setReport] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [proctor, setProctor] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [openingReport, setOpeningReport] = useState(false);

  useEffect(() => {
    document.title = "Interview report - Ample Hire";
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    Promise.allSettled([
      getInterview(id),
      getInterview(id, "report-json"),
      getInterview(id, "transcript"),
      getInterview(id, "recording-url"),
      getInterview(id, "proctor-events"),
    ]).then((results) => {
      if (cancelled) return;
      const [d, r, t, rec, pe] = results;
      const errs = {};

      if (d.status === "fulfilled") {
        setDetails(d.value.data?.data ?? d.value.data ?? null);
      } else {
        errs.details = "Failed to load interview details.";
      }

      if (r.status === "fulfilled") {
        const payload = r.value.data?.data ?? r.value.data ?? {};
        setReport(payload.report ?? payload ?? null);
      } else {
        errs.report = "Failed to load report.";
      }

      if (t.status === "fulfilled") {
        setTranscript(t.value.data?.data ?? t.value.data ?? null);
      } else {
        errs.transcript = "Failed to load transcript.";
      }

      if (rec.status === "fulfilled") {
        const payload = rec.value.data?.data ?? rec.value.data ?? {};
        setRecordingUrl(payload?.url ?? null);
      } else {
        errs.recording = "Failed to load recording.";
      }

      if (pe.status === "fulfilled") {
        setProctor(pe.value.data?.data ?? pe.value.data ?? null);
      } else {
        // Distinguished from "no events": a failed call means we don't know
        // what happened, which must not be shown as a clean interview.
        errs.proctor = "Failed to load integrity data.";
      }

      setErrors(errs);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const openFullReport = async () => {
    if (openingReport || !id) return;
    setOpeningReport(true);
    try {
      const res = await getInterview(id, "report-url");
      const url = (res.data?.data ?? res.data)?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else alert("No report URL returned by the server.");
    } catch {
      alert("Failed to fetch report URL.");
    } finally {
      setOpeningReport(false);
    }
  };

  if (!id) {
    return (
      <div className="page-enter mx-auto max-w-3xl px-6 py-10">
        <p className="text-sm text-slate-500">Missing interview id.</p>
      </div>
    );
  }

  const recTone = recommendationTone(details?.recommendation);
  const transcriptEntries = Array.isArray(transcript?.entries)
    ? transcript.entries
    : Array.isArray(transcript)
      ? transcript
      : [];
  const notableMoments = Array.isArray(report?.notable_moments)
    ? report.notable_moments
    : [];
  const redFlags = Array.isArray(report?.red_flags) ? report.red_flags : [];
  const suggestedQA = Array.isArray(report?.suggested_question_answers)
    ? report.suggested_question_answers
    : [];
  const askedCount = suggestedQA.filter((q) => q?.asked).length;

  return (
    <div className="page-enter mx-auto max-w-7xl px-6 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => nav(-1)}
            className="mb-1 text-xs text-slate-500 hover:text-slate-800"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-slate-900">
            {details?.candidate_name || (loading ? "Loading…" : "Interview")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {details?.job_title || "—"}
            {details?.job_level ? ` · ${details.job_level}` : ""}
          </p>
        </div>
        <button
          onClick={openFullReport}
          disabled={openingReport}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <ExternalLinkIcon />
          {openingReport ? "Opening…" : "View full report"}
        </button>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* LEFT column */}
        <aside className="space-y-5 lg:col-span-5 xl:col-span-4">
          {/* AI analysis */}
          <SectionCard>
            <header className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <span className="text-violet-500">
                <SparkleIcon />
              </span>
              AI analysis
            </header>
            {details?.recommendation && (
              <div
                className={`mb-3 inline-block rounded-full border ${recTone.bg} ${recTone.border} px-3 py-1 text-xs font-semibold ${recTone.fg}`}
              >
                {details.recommendation}
              </div>
            )}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {details?.analysis_summary ||
                (loading
                  ? "Loading analysis…"
                  : errors.details
                    ? "Analysis unavailable."
                    : "No analysis available.")}
            </p>
            {details?.overall_score != null && (
              <div className="mt-5 flex justify-center">
                <ScoreRing
                  score={details.overall_score}
                  size={88}
                  thickness={8}
                />
              </div>
            )}
            {details?.overall_score != null && (
              <div className="mt-1 text-center text-[11px] text-slate-400">
                / 100
              </div>
            )}
          </SectionCard>

          {/* Suggested questions & answers — only rendered when the report
              actually carries this key with at least one entry. */}
          {suggestedQA.length > 0 && (
            <SectionCard>
              <header className="mb-4 flex items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <span className="text-violet-500">
                    <ChecklistIcon />
                  </span>
                  Suggested questions &amp; answers
                </h3>
                <span className="shrink-0 text-xs text-slate-400">
                  {askedCount}/{suggestedQA.length} asked
                </span>
              </header>
              <ul className="space-y-4">
                {suggestedQA.map((qa, i) => {
                  const score = qa?.score;
                  return (
                    <li
                      key={i}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 text-sm font-semibold leading-relaxed text-slate-900">
                          <span className="mr-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Q{i + 1}
                          </span>
                          {qa?.question}
                        </p>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {qa?.asked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              <CheckIcon />
                              Asked
                            </span>
                          )}
                          {score != null && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              {score}/100
                            </span>
                          )}
                        </div>
                      </div>
                      {qa?.candidate_answer && (
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            Candidate answer
                          </p>
                          <p className="mt-1 border-l-2 border-violet-200 pl-3 text-sm leading-relaxed text-slate-700">
                            {qa.candidate_answer}
                          </p>
                        </div>
                      )}
                      {qa?.evaluation && (
                        <div className="mt-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            Evaluation
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-slate-500">
                            {qa.evaluation}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </SectionCard>
          )}

          {/* Notable moments — rendered as a conversation (avatar + bubble),
              mirroring the right-column Transcript style. */}
          {notableMoments.length > 0 && (
            <SectionCard>
              <header className="mb-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Notable moments
              </header>
              <ul className="space-y-5">
                {notableMoments.map((m, i) => {
                  const label = speakerLabel(m.speaker || m.role);
                  const ts = m.timestamp_seconds ?? m.start_ts ?? m.ts;
                  const quote = m.quote || m.text;
                  const note = m.note || m.explanation || m.reason;
                  return (
                    <li key={i} className="flex gap-3">
                      <Avatar kind={label} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 text-xs">
                          <span
                            className={`font-semibold ${
                              label === "Interviewer"
                                ? "text-violet-700"
                                : "text-emerald-700"
                            }`}
                          >
                            {label}
                          </span>
                          {ts != null && (
                            <span className="text-slate-400">
                              {fmtMmSs(ts)}
                            </span>
                          )}
                        </div>
                        {quote && (
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
                            “{quote}”
                          </p>
                        )}
                        {note && (
                          <p className="mt-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs leading-relaxed text-slate-600">
                            {note}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={openFullReport}
                  disabled={openingReport}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                >
                  <ExternalLinkIcon />
                  {openingReport ? "Opening…" : "View full report"}
                </button>
                <span className="text-xs text-slate-400">
                  Opens in a new tab
                </span>
              </div>
            </SectionCard>
          )}

          {/* Recording */}
          <SectionCard>
            <header className="mb-3 text-sm font-semibold text-slate-700">
              Recording
            </header>
            {recordingUrl ? (
              <>
                <video
                  src={recordingUrl}
                  controls
                  className="w-full rounded-md bg-black"
                />
                <p className="mt-2 text-xs text-slate-400">
                  Presigned URL — expires after a short period.
                </p>
              </>
            ) : errors.recording ? (
              <p className="text-sm text-slate-400">Recording unavailable.</p>
            ) : (
              <p className="text-sm text-slate-400">
                {loading ? "Loading recording…" : "No recording available."}
              </p>
            )}
          </SectionCard>

          {/* Details */}
          <SectionCard>
            <header className="mb-4 text-sm font-semibold text-slate-700">
              Details
            </header>
            <dl className="space-y-3">
              <DetailRow
                label="Duration"
                value={
                  details
                    ? `${fmtDuration(details.duration_seconds)}${
                        details.minutes_target
                          ? ` / target ${details.minutes_target}m`
                          : ""
                      }`
                    : "—"
                }
              />
              <DetailRow
                label="Started"
                value={timeAgo(details?.started_at)}
              />
              <DetailRow label="Ended" value={timeAgo(details?.ended_at)} />
              <DetailRow
                label="Outcome"
                value={titleCase(details?.interview_outcome)}
              />
              <DetailRow
                label="Analyzed"
                value={timeAgo(details?.analysis_ended_at)}
              />
              <DetailRow
                label="Room"
                value={details?.room_name || "—"}
                mono
              />
              <DetailRow
                label="Experience"
                value={
                  details?.candidate_years_experience != null
                    ? `${details.candidate_years_experience} year${
                        details.candidate_years_experience !== 1 ? "s" : ""
                      }`
                    : "—"
                }
              />
            </dl>
          </SectionCard>

          {/* Red flags — destructive palette; only renders when the report
              actually has flags so we don't draw a scary empty card. */}
          {redFlags.length > 0 && (
            <SectionCard className="border-red-200 bg-red-50/40">
              <header className="mb-3 flex items-center gap-2 text-sm font-semibold text-red-700">
                <WarningIcon />
                Red flags
              </header>
              <ul className="space-y-2.5">
                {redFlags.map((flag, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                    <p className="text-sm leading-relaxed text-red-900">
                      {flag}
                    </p>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}

          {/* Always rendered — unlike red flags, the *absence* of integrity data
              is itself information a reviewer needs, so this card never hides. */}
          <IntegritySection data={proctor} error={errors.proctor} loading={loading} />
        </aside>

        {/* RIGHT column — Transcript */}
        <main className="lg:col-span-7 xl:col-span-8">
          <SectionCard className="min-h-[500px]">
            <header className="mb-5 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <DocIcon />
                Transcript
              </h2>
              <span className="text-xs text-slate-400">
                {details?.transcript_entries ?? transcriptEntries.length} turns
              </span>
            </header>
            {errors.transcript ? (
              <p className="text-sm text-slate-400">Transcript unavailable.</p>
            ) : transcriptEntries.length === 0 ? (
              <p className="text-sm text-slate-400">
                {loading
                  ? "Loading transcript…"
                  : "No transcript entries available."}
              </p>
            ) : (
              <ul className="space-y-5">
                {transcriptEntries.map((t, i) => {
                  const label = speakerLabel(t.speaker ?? t.role);
                  const ts = t.start_ts ?? t.ts;
                  return (
                    <li key={i} className="flex gap-3">
                      <Avatar kind={label} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 text-xs">
                          <span
                            className={`font-semibold ${
                              label === "Interviewer"
                                ? "text-violet-700"
                                : "text-emerald-700"
                            }`}
                          >
                            {label}
                          </span>
                          {ts != null && (
                            <span className="text-slate-400">
                              {fmtMmSs(ts)}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
                          {t.text || ""}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </main>
      </div>
    </div>
  );
}
